import {FunctionComponent} from 'react';
import {clsx} from 'clsx';
import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {AdvisorProfileFormValues} from '@/components/advisor-profile-form/interfaces/form-values';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import Radio from '@/components/form/radio';
import {FieldMetadata} from '@/interfaces/form';

interface UserGeneralInfoSectionProps {
  fieldsMetadata: {
    cons1_lastName: FieldMetadata;
    cons2_firstName: FieldMetadata;
    cons16_isFundServe: FieldMetadata;
  };
  getFieldMessage: (fieldId: string) => string | undefined;
  formik: Pick<
    FormikProps<Partial<AdvisorProfileFormValues>>,
    'handleChange' | 'handleBlur' | 'setFieldValue' | 'values' | 'errors'
  >;
}

export const UserGeneralInfoSection: FunctionComponent<UserGeneralInfoSectionProps> = ({
  fieldsMetadata,
  getFieldMessage,
  formik: {handleChange, handleBlur, setFieldValue, values, errors},
}) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  return (
    <div
      className={clsx(
        'grid grid-cols-1 items-start gap-4',
        fieldsMetadata.cons16_isFundServe.isHidden && 'md:grid-cols-2',
        !fieldsMetadata.cons16_isFundServe.isHidden && 'md:grid-cols-3',
      )}
    >
      <FormControl
        isRequired={fieldsMetadata.cons1_lastName.isRequired}
        isInError={!!errors.cons1_lastName}
        errors={[errors.cons1_lastName ?? '']}
        warningMessage={getFieldMessage('cons1_lastName')}
      >
        <FieldWrapper
          label={t('label-lastname')}
          labelMultiline={false}
          identifier="CONS1"
          hidden={fieldsMetadata.cons1_lastName.isHidden}
        >
          <Input
            data-testid="CONS1-input"
            id="CONS1"
            maxLength={25}
            name="CONS1"
            onBlur={handleBlur('cons1_lastName')}
            onChange={handleChange('cons1_lastName')}
            required
            readOnly={fieldsMetadata.cons1_lastName.isReadOnly}
            value={values.cons1_lastName}
          />
        </FieldWrapper>
      </FormControl>

      <FormControl
        isRequired={fieldsMetadata.cons2_firstName.isRequired}
        isInError={!!errors.cons2_firstName}
        errors={[errors.cons2_firstName ?? '']}
        warningMessage={getFieldMessage('cons2_firstName')}
      >
        <FieldWrapper
          label={t('label-firstname')}
          labelMultiline={false}
          identifier="CONS2"
          hidden={fieldsMetadata.cons2_firstName.isHidden}
        >
          <Input
            data-testid="CONS2-input"
            id="CONS2"
            maxLength={25}
            name="CONS2"
            onBlur={handleBlur('cons2_firstName')}
            onChange={handleChange('cons2_firstName')}
            required
            readOnly={fieldsMetadata.cons2_firstName.isReadOnly}
            value={values.cons2_firstName}
          />
        </FieldWrapper>
      </FormControl>

      <FormControl
        isRequired={fieldsMetadata.cons16_isFundServe.isRequired}
        isInError={!!errors.cons16_isFundServe}
        errors={[errors.cons16_isFundServe ?? '']}
      >
        <FieldWrapper
          label={t('label-is-fundserv')}
          labelMultiline={false}
          identifier="CONS16"
          useFieldset
          hidden={fieldsMetadata.cons16_isFundServe.isHidden}
        >
          <div className="flex flex-row gap-4 py-4" data-testid="CONS16-control">
            <Radio
              data-testid="CONS16-control-option-oui"
              type="radio"
              label={t('label-yes')}
              name="CONS16"
              value="true"
              checked={values.cons16_isFundServe === 'true'}
              onChange={() => {
                setFieldValue('cons16_isFundServe', 'true');
              }}
              onBlur={handleBlur}
              readOnly={fieldsMetadata.cons16_isFundServe.isReadOnly}
              data-testvalue="true"
            />

            <Radio
              data-testid="CONS16-control-option-non"
              type="radio"
              label={t('label-no')}
              name="CONS16"
              value="false"
              checked={values.cons16_isFundServe === 'false'}
              onChange={() => {
                setFieldValue('cons16_isFundServe', 'false');
              }}
              onBlur={handleBlur}
              readOnly={fieldsMetadata.cons16_isFundServe.isReadOnly}
              data-testvalue="false"
            />
          </div>
        </FieldWrapper>
      </FormControl>
    </div>
  );
};
